import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { personHealthProfileApi } from '../../services/personHealthProfileApi';

export const personHealthProfileKeys = {
  all: ['personHealthProfiles'] as const,
  lists: () => [...personHealthProfileKeys.all, 'list'] as const,
  list: (filters?: { pageNumber?: number; pageSize?: number; search?: string }) =>
    [...personHealthProfileKeys.lists(), filters] as const,
  byPerson: (personId: string) => [...personHealthProfileKeys.all, 'byPerson', personId] as const,
  details: () => [...personHealthProfileKeys.all, 'detail'] as const,
  detail: (id: string) => [...personHealthProfileKeys.details(), id] as const,
};

export function useGetPersonHealthProfile(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: personHealthProfileKeys.detail(id),
    queryFn: async () => {
      const response = await personHealthProfileApi.getById(id);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch health profile'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!id,
  });
}

export function useGetPersonHealthProfileByPersonId(personId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: personHealthProfileKeys.byPerson(personId),
    queryFn: async () => {
      const response = await personHealthProfileApi.getByPersonId(personId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch health profile'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!personId,
  });
}

export function useGetPersonHealthProfiles() {
  return useQuery({
    queryKey: personHealthProfileKeys.lists(),
    queryFn: async () => {
      const response = await personHealthProfileApi.getList();
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch health profiles'}`);
      }
      return response;
    },
  });
}

export function useGetPersonHealthProfilesPaged(
  pageNumber: number = 1,
  pageSize: number = 10,
  search?: string
) {
  return useQuery({
    queryKey: personHealthProfileKeys.list({ pageNumber, pageSize, search }),
    queryFn: async () => {
      const response = await personHealthProfileApi.getPagedList(pageNumber, pageSize, search);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch health profiles'}`);
      }
      return response;
    },
  });
}
